/**
 * CR-5905: Demo Data Roll-Forward
 *
 * Reinstates the legacy "Automated system to upload Supplier Portal data"
 * utility from the legacy platform — Platform Admin can shift demo data by
 * ±1 calendar month, per table or across all configured tables, so demo
 * environments stay current without re-uploading datasets each month.
 *
 * Backend gates the endpoint with IsHelixUser; this component is rendered
 * inside the (already Platform-Admin-only) Data Import tab.
 */
import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../ui/alert-dialog';
import { CalendarClock, Loader2, Plus, Minus } from 'lucide-react';
import { toast } from 'sonner';
import { demoDateShiftAPI, type DemoDateShiftTable } from '../../../lib/api';
import { useFormatters } from '../../../contexts/PreferencesContext';

type Direction = 'plus' | 'minus';

interface PendingAction {
  direction: Direction;
  tableKey?: string;
  tableLabel: string;
}

export function DemoDataRollForwardSection() {
  const [tables, setTables] = useState<DemoDateShiftTable[]>([]);
  const [loading, setLoading] = useState(true);
  const { formatDateTime } = useFormatters();
  const [runningKey, setRunningKey] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingAction | null>(null);

  const loadTables = async () => {
    try {
      const res = await demoDateShiftAPI.list();
      setTables(res.data || []);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to load demo data tables';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTables();
  }, []);

  const runShift = async (direction: Direction, tableKey?: string) => {
    const key = tableKey ?? '__all__';
    setRunningKey(key);
    try {
      const res = await demoDateShiftAPI.shift(direction, tableKey);
      const verb = direction === 'plus' ? 'Plus one month' : 'Less one month';
      const rows = `${res.data.total_rows_affected} row${res.data.total_rows_affected === 1 ? '' : 's'} shifted`;
      // The response carries the dispatch status of the async snapshot/trend
      // rebuild (CR-6240). A skipped rebuild means trend charts keep serving
      // pre-shift months — that must not hide behind a success toast.
      const recalc = res.data.recalc?.snapshot_recalc;
      const flushErrors = res.data.recalc?.errors ?? [];
      if (flushErrors.length > 0) {
        // The inline cache flush partially failed: live KPI cards may keep
        // pre-shift figures and nothing self-heals them. Worse than a
        // skipped rebuild, so it takes precedence (one toast, not two).
        toast.warning(
          `${verb}: ${rows}. Dashboard cache refresh hit errors (${flushErrors.join('; ')}) — KPI cards may keep pre-shift figures.${recalc && recalc !== 'queued' ? ` Trend rebuild ${recalc}.` : ''}`,
        );
      } else if (recalc && recalc !== 'queued') {
        toast.warning(
          `${verb}: ${rows}. Dashboard trend rebuild ${recalc} — trend charts keep pre-shift months until it runs.`,
        );
      } else if (recalc === 'queued') {
        toast.success(`${verb}: ${rows} — dashboard trend rebuild queued`);
      } else {
        toast.success(`${verb}: ${rows}`);
      }
      await loadTables();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Date shift failed';
      toast.error(msg);
    } finally {
      setRunningKey(null);
      setPending(null);
    }
  };

  const ask = (action: PendingAction) => setPending(action);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CalendarClock className="h-5 w-5" />
          Demo Data — Date Roll Forward
        </CardTitle>
        <CardDescription>
          Platform Admin utility. Shift dates on demo data tables by ±1 calendar
          month so demo environments stay current. Each action is audited.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-3">
          <Button
            variant="outline"
            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            disabled={runningKey !== null}
            onClick={() => ask({ direction: 'plus', tableLabel: 'All tables' })}
          >
            {runningKey === '__all__' ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Plus className="h-4 w-4 mr-2" />
            )}
            Plus one month (All Tables)
          </Button>
          <Button
            variant="outline"
            className="border-rose-300 text-rose-700 hover:bg-rose-50"
            disabled={runningKey !== null}
            onClick={() => ask({ direction: 'minus', tableLabel: 'All tables' })}
          >
            {runningKey === '__all__' ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Minus className="h-4 w-4 mr-2" />
            )}
            Less one month (All Tables)
          </Button>
        </div>

        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Table Name</TableHead>
                <TableHead className="text-right">Plus Count</TableHead>
                <TableHead className="text-right">Minus Count</TableHead>
                <TableHead>Last Action</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                    <Loader2 className="h-5 w-5 animate-spin inline-block mr-2" />
                    Loading demo tables…
                  </TableCell>
                </TableRow>
              ) : tables.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                    No demo tables configured.
                  </TableCell>
                </TableRow>
              ) : (
                tables.map((t) => (
                  <TableRow key={t.id}>
                    <TableCell className="font-mono text-xs">{t.display_name}</TableCell>
                    <TableCell className="text-right">{t.plus_count}</TableCell>
                    <TableCell className="text-right">{t.minus_count}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {t.last_action ? (
                        <span className="flex items-center gap-1">
                          <Badge variant={t.last_action === 'plus' ? 'default' : 'secondary'} className="text-[10px]">
                            {t.last_action === 'plus' ? '+1mo' : '-1mo'}
                          </Badge>
                          {t.last_actor_name ? `by ${t.last_actor_name}` : ''}
                          {t.last_action_at ? ` · ${formatDateTime(t.last_action_at)}` : ''}
                          {t.last_affected_rows ? ` · ${t.last_affected_rows} rows` : ''}
                        </span>
                      ) : (
                        <span>Never run</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                          disabled={runningKey !== null}
                          onClick={() =>
                            ask({ direction: 'plus', tableKey: t.table_key, tableLabel: t.display_name })
                          }
                        >
                          {runningKey === t.table_key ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Plus className="h-3 w-3" />
                          )}
                          <span className="ml-1">Plus one month</span>
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-rose-300 text-rose-700 hover:bg-rose-50"
                          disabled={runningKey !== null}
                          onClick={() =>
                            ask({ direction: 'minus', tableKey: t.table_key, tableLabel: t.display_name })
                          }
                        >
                          {runningKey === t.table_key ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Minus className="h-3 w-3" />
                          )}
                          <span className="ml-1">Less one month</span>
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>

      <AlertDialog open={pending !== null} onOpenChange={(open) => !open && setPending(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Confirm {pending?.direction === 'plus' ? 'Plus one month' : 'Less one month'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              This shifts all configured date fields on <strong>{pending?.tableLabel}</strong>{' '}
              by {pending?.direction === 'plus' ? '+1' : '-1'} calendar month. Demo data only —
              the action is logged with your user ID and timestamp.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={runningKey !== null}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={runningKey !== null}
              onClick={() => pending && runShift(pending.direction, pending.tableKey)}
            >
              {runningKey !== null ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : null}
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
